import React, { useMemo } from 'react';
import { Select } from 'antd';
import { MATCH_MODES, describeBaselineRound, monthsBetween } from '../../../../../../constants';

const { Option } = Select;

// `projects` straight from the API response — { value, label, status,
// totalParticipantsCount, isPrevious?, isFirst? } — keyed by project id, since
// month labels repeat.
// The check mark isn't tracked by hand: antd already marks the selected <li>
// with .ant-select-item-option-selected, so CSS alone shows/hides it.
const baselineOptions = (projects) => projects.map((round) => {
    const details = [describeBaselineRound(round), round.status, `${round.totalParticipantsCount} participants`].filter(Boolean);
    return (
        <Option key={round.value} value={round.value} label={round.label}>
            <span className='trend-compared__option'>
                <span className='trend-compared__option-check'></span>
                <span className='trend-compared__option-text'>
                    <span className='trend-compared__option-title'>{round.label}</span>
                    <span className='trend-compared__option-sub'>{details.join(' · ')}</span>
                </span>
            </span>
        </Option>
    );
});

// One compared leader. Connected on its own (see index.js) to just this
// leader's hand-picked baseline, so changing one dropdown re-renders this row
// only — not the table and not the other rows.
const ComparedRow = (props) => {
    const { leader, pickedValue, matchMode } = props;
    const projects = leader.projects || [];

    // Options only depend on the leader's projects, which keep their identity
    // across re-renders — so a baseline pick doesn't rebuild them.
    const options = useMemo(() => baselineOptions(projects), [projects]);

    // A leader with no earlier cycle can't be compared — the API shouldn't
    // send one, but there'd be nothing to show.
    if (!projects.length) return null;

    // Hand-picked baseline first; otherwise picked by position in the list the
    // API sent: "their previous 360" → last option, "their very first" → first.
    const baseline = (pickedValue && projects.find((round) => round.value === pickedValue))
        || (matchMode === MATCH_MODES.FIRST ? projects[projects.length - 1] : projects[0]);
    const gap = monthsBetween(baseline.label, leader.label);

    // How many raters each side of the comparison rests on — the picked earlier
    // round's, then the current one's. Both are totalParticipantsCount: on the
    // project for the baseline, on the leader itself for the current 360.
    const raters = baseline.totalParticipantsCount != null && leader.totalParticipantsCount != null
        ? `${baseline.totalParticipantsCount}→${leader.totalParticipantsCount} raters`
        : null;

    // Neither half is guaranteed — an unparseable pair of labels drops the gap,
    // a response without the counts drops the raters — so the separator is only
    // written between the halves that survived.
    const meta = [gap != null ? `${gap} mo` : null, raters].filter(Boolean).join(' · ');
    return (
        <div className='trend-table__row'>
            <div className='trend-table__person'>
                <span className='trend-table__person-name'>{leader.fullName || leader.selfEmail}</span>
                <span className='trend-table__person-email'>{leader.selfEmail}</span>
            </div>

            <div className='trend-compared__match'>
                {/* Only one earlier 360 on record means there's nothing to
                    switch between, so the baseline reads as plain text. */}
                {projects.length > 1 ? (
                    <Select
                        className='trend-compared__baseline'
                        dropdownClassName='trend-compared__dropdown'
                        dropdownMatchSelectWidth={280}

                        optionLabelProp='label'
                        value={baseline.value}
                        onChange={(value) => props.onChangeBaseline(leader.value, value)}
                    >
                        {options}
                    </Select>
                ) : (
                    <span className='trend-compared__baseline-text'>{baseline.label}</span>
                )}
                <span className='trend-compared__arrow'>{"->"}</span>
                <span className='trend-compared__current'>{leader.label}</span>
                {meta && <span className='trend-compared__meta'>{meta}</span>}
            </div>
        </div>
    );
};

export default ComparedRow;
